import { Edit, RemoveCircle } from '@mui/icons-material';
import { Box, FormControl, FormLabel, Grid, IconButton, OutlinedInput, Paper, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssignmentType, DispatchReviewDto, FreightBillingLineItemDto, OrderDto, OrderReviewLineItemDto, PricingMethodType } from '../../../dtos';
import { useCreateBillingRatesFreightLineItemBillingRatesMutation } from '../../../store/generated/generatedApi';
import { LineItemTypes, QuoteType, formatCurrency } from '../../../util';
import { PricingMethodTypeSelect } from '../../CommonInputs';
import { FormInput, FormNumberInput } from '../../CoreLib/library';
import { BillingRateFormDialog } from '../BillingRateForm/BillingRatesFormDialog';

export interface IFreightBillingTableProps {
    disabled?: boolean;
    orderReviewLineItem: OrderReviewLineItemDto;
    order: OrderDto;
    dispatchReview: DispatchReviewDto;
    removeFreightBillingLineItem: (index: number) => void;
    updateFreightBillingLineItemField: <K extends keyof FreightBillingLineItemDto>(index: number, key: K, value: FreightBillingLineItemDto[K]) => void;
    updateFreightBillingLineItem: (index: number, freightBillingLineItem: FreightBillingLineItemDto) => void;
    freightErrors: Map<number, Map<keyof FreightBillingLineItemDto, string>>;
}

export const FreightBillingLineItemTable: React.FC<IFreightBillingTableProps> = ({
    disabled,
    orderReviewLineItem,
    order,
    dispatchReview,
    freightErrors,
    removeFreightBillingLineItem,
    updateFreightBillingLineItemField,
    updateFreightBillingLineItem
}) => {
    const freightBillingLineItems = useMemo(() => dispatchReview.freightBillingLineItems ?? [], [dispatchReview.freightBillingLineItems]);
    // Read via refs (rather than as useCallback deps) so handleFreightBillingLineItemChange keeps a
    // stable identity across renders. The parent rebuilds these props on every keystroke anywhere in
    // the ticket, and an unstable onChange tears down FormNumberInput's internal debounce mid-typing,
    // silently dropping or delaying the pending Amount recalculation (RK-7419).
    const freightBillingLineItemsRef = useRef(freightBillingLineItems);
    freightBillingLineItemsRef.current = freightBillingLineItems;
    const updateFreightBillingLineItemFieldRef = useRef(updateFreightBillingLineItemField);
    updateFreightBillingLineItemFieldRef.current = updateFreightBillingLineItemField;
    const [isBillingRatesFormDialogOpen, setIsBillingRatesFormDialogOpen] = useState<number | undefined>(undefined);
    const [getBillingRates] = useCreateBillingRatesFreightLineItemBillingRatesMutation();
    const isAssignedToDriver = useMemo(() => dispatchReview.assignmentType === AssignmentType.Driver, [dispatchReview.assignmentType]);

    const [formQuantityTotal, setFormQuantityTotal] = useState<number>(0);
    const [formAmountTotal, setFormAmountTotal] = useState<number>(0);
    const [formHaulerTotal, setFormHaulerTotal] = useState<number>(0);

    useEffect(() => {
        var quantityTotal = 0;
        var amountTotal = 0;
        var haulerTotal = 0;
        var PRECISION_MULTIPLIER = 100;
        freightBillingLineItems.forEach((freightBillingLineItem) => {
            quantityTotal += Math.round((freightBillingLineItem.quantity ?? 0) * PRECISION_MULTIPLIER);
            amountTotal += freightBillingLineItem.salesPrice ?? freightBillingLineItem.quoteRate ?? 0;
            
            if (isAssignedToDriver) {
                haulerTotal += freightBillingLineItem.driverSalesPrice ?? 0;
            } else {
                haulerTotal += freightBillingLineItem.brokerSalesPrice ?? 0;
            }
        });
        setFormQuantityTotal(quantityTotal / PRECISION_MULTIPLIER);
        setFormAmountTotal(amountTotal);
        setFormHaulerTotal(haulerTotal);
    }, [freightBillingLineItems, isAssignedToDriver]);

    const handleFreightBillingLineItemChange = useCallback(
        <K extends keyof FreightBillingLineItemDto>(index: number, key: K, value: FreightBillingLineItemDto[K]) => {
            updateFreightBillingLineItemFieldRef.current(index, key, value);
            if (key === 'pricingMethodType') {
                updateFreightBillingLineItemFieldRef.current(index, 'isMaterialOnly', value === PricingMethodType.MaterialOnly);
                if (value === PricingMethodType.MaterialOnly) {
                    updateFreightBillingLineItemFieldRef.current(index, 'fuelSurchargePercent', undefined);
                }
            }
            if (key === 'fuelSurchargePercent') {
                if (value === undefined || value === null) {
                    updateFreightBillingLineItemFieldRef.current(index, 'fuelSurchargePercent', undefined);
                } else if (typeof value === 'number') {
                    const clamped = Math.min(100, Math.max(0, value));
                    updateFreightBillingLineItemFieldRef.current(index, 'fuelSurchargePercent', clamped);
                }
            }
            // If the quantity or pricing method type changes, we need to recalculate the billing rates
            if (key === 'quantity' || key === 'pricingMethodType') {
                const updatedFreightBillingLineItem = { ...freightBillingLineItemsRef.current[index] };
                updatedFreightBillingLineItem[key] = value;
                if (key === 'pricingMethodType') {
                    updatedFreightBillingLineItem.isMaterialOnly = value === PricingMethodType.MaterialOnly;
                }
                getBillingRates(updatedFreightBillingLineItem).unwrap().then((response) => {
                    updateFreightBillingLineItemFieldRef.current(index, 'quoteRate', response.quoteRate);
                    updateFreightBillingLineItemFieldRef.current(index, 'driverRate', response.driverRate);
                    updateFreightBillingLineItemFieldRef.current(index, 'brokerRate', response.brokerRate);
                    updateFreightBillingLineItemFieldRef.current(index, 'salesPrice', response.salesPrice);
                    updateFreightBillingLineItemFieldRef.current(index, 'flatRateApplied', response.flatRateApplied);
                    updateFreightBillingLineItemFieldRef.current(index, 'flatDriverRate', response.flatDriverRate);
                    updateFreightBillingLineItemFieldRef.current(index, 'driverFlatRateApplied', response.driverFlatRateApplied);
                    updateFreightBillingLineItemFieldRef.current(index, 'driverSalesPrice', response.driverSalesPrice);
                    updateFreightBillingLineItemFieldRef.current(index, 'flatBrokerRate', response.flatBrokerRate);
                    updateFreightBillingLineItemFieldRef.current(index, 'brokerFlatRateApplied', response.brokerFlatRateApplied);
                    updateFreightBillingLineItemFieldRef.current(index, 'brokerSalesPrice', response.brokerSalesPrice);
                }).catch(() => {
                    // A failed recalculation leaves Amount showing its previous value, which is
                    // indistinguishable from a fresh one. Swallow the rejection so it isn't unhandled.
                });
            }
        },
        [getBillingRates]
    );

    // FormNumberInput's internal onChange debounce (CoreLib library.js) is keyed on the onChange
    // reference, so a fresh inline handler each render would tear the debounce down before it fires.
    // Caching one handler per row keeps that reference stable, and reusing it for onBlur flushes the
    // recalculation immediately when the user tabs away instead of waiting on the debounce timer.
    const quantityChangeHandlersRef = useRef(new Map<number, (event: React.ChangeEvent<HTMLInputElement>) => void>());
    const quantityBlurHandlersRef = useRef(new Map<number, React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>());

    const commitQuantity = useCallback(
        (index: number, rawValue: string, valueAsNumber: number) => {
            // An empty field is an intentional clear; an incomplete number (e.g. "22." while still
            // typing a decimal) is not, and valueAsNumber is NaN for both — the raw string is what
            // tells them apart. Leave the field alone rather than clearing it mid-edit.
            if (isNaN(valueAsNumber) && rawValue !== '') {
                return;
            }
            const quantity = isNaN(valueAsNumber) ? undefined : valueAsNumber;
            // Blur fires whether or not the user actually edited the field. Committing an unchanged
            // value would flag the form dirty (triggering the unsaved-changes prompt on exit) and
            // spend a billing-rate round trip for nothing. It also makes the trailing debounced call
            // from the change path a no-op once blur has already committed the same value.
            if (quantity === freightBillingLineItemsRef.current[index]?.quantity) {
                return;
            }
            handleFreightBillingLineItemChange(index, 'quantity', quantity);
        },
        [handleFreightBillingLineItemChange]
    );

    // The handler caches below key on row index only, so they must be dropped if commitQuantity's
    // identity ever changes — otherwise a cached closure would keep calling a stale version of it.
    const cachedCommitQuantityRef = useRef(commitQuantity);
    if (cachedCommitQuantityRef.current !== commitQuantity) {
        cachedCommitQuantityRef.current = commitQuantity;
        quantityChangeHandlersRef.current.clear();
        quantityBlurHandlersRef.current.clear();
    }

    const getQuantityChangeHandler = useCallback(
        (index: number) => {
            let handler = quantityChangeHandlersRef.current.get(index);
            if (!handler) {
                handler = (event) => commitQuantity(index, event.target.value, event.target.valueAsNumber);
                quantityChangeHandlersRef.current.set(index, handler);
            }
            return handler;
        },
        [commitQuantity]
    );

    const getQuantityBlurHandler = useCallback(
        (index: number) => {
            let handler = quantityBlurHandlersRef.current.get(index);
            if (!handler) {
                handler = (event) => {
                    const input = event.target as HTMLInputElement;
                    commitQuantity(index, input.value, input.valueAsNumber);
                };
                quantityBlurHandlersRef.current.set(index, handler);
            }
            return handler;
        },
        [commitQuantity]
    );

    const handleRatesUpdated = useCallback((index: number, updatedFreightBillingLineItem: FreightBillingLineItemDto) => {
        getBillingRates(updatedFreightBillingLineItem).unwrap().then((response) => {
            updatedFreightBillingLineItem.quoteRate = response.quoteRate;
            updatedFreightBillingLineItem.driverRate = response.driverRate;
            updatedFreightBillingLineItem.brokerRate = response.brokerRate;
            updatedFreightBillingLineItem.salesPrice = response.salesPrice;
            updatedFreightBillingLineItem.flatRateApplied = response.flatRateApplied;
            updatedFreightBillingLineItem.driverRate = response.driverRate;
            updatedFreightBillingLineItem.flatDriverRate = response.flatDriverRate;
            updatedFreightBillingLineItem.driverFlatRateApplied = response.driverFlatRateApplied;
            updatedFreightBillingLineItem.driverSalesPrice = response.driverSalesPrice;
            updatedFreightBillingLineItem.brokerRate = response.brokerRate;
            updatedFreightBillingLineItem.flatBrokerRate = response.flatBrokerRate;
            updatedFreightBillingLineItem.brokerFlatRateApplied = response.brokerFlatRateApplied;
            updatedFreightBillingLineItem.brokerSalesPrice = response.brokerSalesPrice;
            updateFreightBillingLineItem(index, updatedFreightBillingLineItem);
        });
    }, [getBillingRates, updateFreightBillingLineItem]);

    const isFob = useMemo(() => order?.quote?.type === QuoteType.FOB, [order?.quote?.type]);
    const isMaterialLineItem = useMemo(() => orderReviewLineItem.lineItem?.type === LineItemTypes.Material, [orderReviewLineItem.lineItem?.type]);
    const isDumpLineItem = useMemo(() => orderReviewLineItem.lineItem?.type === LineItemTypes.Dump, [orderReviewLineItem.lineItem?.type]);
    const isStandardMaterialLineItem = useMemo(() => isMaterialLineItem && !isFob, [isFob, isMaterialLineItem]);
    const isMaterialOrDumpLineItem = useMemo(() => isMaterialLineItem || isDumpLineItem, [isMaterialLineItem, isDumpLineItem]);

    const isDisabled = useCallback(
        (freightBillingLineItem: FreightBillingLineItemDto) => {
            return disabled || freightBillingLineItem.isInvoiced!;
        },
        [disabled]
    );
    
    const getIsFlatRateApplied = useCallback((freightBillingLineItem: FreightBillingLineItemDto) => {
        return isAssignedToDriver ? freightBillingLineItem.driverFlatRateApplied : freightBillingLineItem.brokerFlatRateApplied;
    }, [isAssignedToDriver]);

    const getHaulerRate = useCallback((freightBillingLineItem: FreightBillingLineItemDto) => {
        if (isAssignedToDriver) {
            return freightBillingLineItem.driverFlatRateApplied ?? freightBillingLineItem.driverRate;
        } else {
            return freightBillingLineItem.brokerFlatRateApplied ?? freightBillingLineItem.brokerRate;
        }
    }, [isAssignedToDriver]);

    const getHaulerTotal = useCallback((freightBillingLineItem: FreightBillingLineItemDto) => {
        return isAssignedToDriver ? freightBillingLineItem.driverSalesPrice : freightBillingLineItem.brokerSalesPrice;
    }, [isAssignedToDriver]);

    return (
        <Box display='flex' flexDirection='column' gap={2}>
            {freightBillingLineItems && freightBillingLineItems.length > 0 && (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                        Total Quantity: {formQuantityTotal}
                    </Typography>
                    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                        Total Amount: {formatCurrency(formAmountTotal)}
                    </Typography>
                    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                        Hauler Total: {formatCurrency(formHaulerTotal)}
                    </Typography>
                </Box>
            )}
            {freightBillingLineItems.map((freightBillingLineItem, freightLineItemIndex) => (
                <Paper 
                    key={`${freightBillingLineItem.id}-${freightLineItemIndex}`}
                    elevation={1}
                    sx={{ 
                        p: 2,
                        pr: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        position: 'relative'
                    }}
                >
                    <Box sx={{ 
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 1
                    }}>
                        <Tooltip title='Remove Freight Billing' placement='top'>
                            <IconButton
                                disabled={isDisabled(freightBillingLineItem)}
                                size='small'
                                color='primary'
                                onClick={() => removeFreightBillingLineItem(freightLineItemIndex)}>
                                <RemoveCircle />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Grid container spacing={1.5}>
                        <Grid container item xs={12} spacing={1.5}>
                            {isStandardMaterialLineItem && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <PricingMethodTypeSelect
                                        selectedPricingMethodType={freightBillingLineItem.pricingMethodType}
                                        onChange={(value) => handleFreightBillingLineItemChange(freightLineItemIndex, 'pricingMethodType', value)}
                                        required
                                        disabled={isDisabled(freightBillingLineItem)}
                                        errorMessage={freightErrors.get(freightLineItemIndex)?.get('pricingMethodType')}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={isStandardMaterialLineItem ? 6 : 4} md={isStandardMaterialLineItem ? 3 : 4}>
                                <FormInput
                                    label='Freight Bill #'
                                    value={freightBillingLineItem.freightBillNumber}
                                    onChange={(e) => handleFreightBillingLineItemChange(freightLineItemIndex, 'freightBillNumber', e.target.value)}
                                    fullWidth
                                    disabled={isDisabled(freightBillingLineItem)}
                                    required
                                    errorText={freightErrors.get(freightLineItemIndex)?.get('freightBillNumber')}
                                    debounceTime={750}
                                />
                            </Grid>
                            <Grid item xs={6} sm={isStandardMaterialLineItem ? 3 : 4} md={isStandardMaterialLineItem ? 3 : 4}>
                                <FormNumberInput
                                    label='Quantity'
                                    name='quantity'
                                    value={freightBillingLineItem.quantity}
                                    onChange={getQuantityChangeHandler(freightLineItemIndex)}
                                    onBlur={getQuantityBlurHandler(freightLineItemIndex)}
                                    fullWidth
                                    disabled={isDisabled(freightBillingLineItem)}
                                    required={isMaterialOrDumpLineItem}
                                    inputProps={{ min: 0 }}
                                    error={!!freightErrors.get(freightLineItemIndex)?.get('quantity')}
                                    errorText={freightErrors.get(freightLineItemIndex)?.get('quantity')}
                                    debounceTime={750}
                                />
                            </Grid>
                            <Grid item xs={6} sm={isStandardMaterialLineItem ? 3 : 4} md={isStandardMaterialLineItem ? 3 : 4}>
                                <FormInput value={freightBillingLineItem.unitOfMeasure} onChange={() => {}} label='Units' fullWidth disabled />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} spacing={1.5}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth disabled>
                                    <FormLabel>
                                        {'Rate ' + (freightBillingLineItem.flatRateApplied && isMaterialLineItem ? '(+ Flat)' : '')}
                                    </FormLabel>
                                    <OutlinedInput
                                        value={formatCurrency(freightBillingLineItem.quoteRate ?? 0)}
                                        disabled
                                        inputProps={{ min: 0 }}
                                        endAdornment={
                                            <Edit
                                                color='secondary'
                                                sx={{ cursor: 'pointer', fontSize: '1.25rem' }}
                                                onClick={() => {
                                                    setIsBillingRatesFormDialogOpen(freightLineItemIndex);
                                                }}
                                            />
                                        }
                                    />
                                </FormControl>
                                <BillingRateFormDialog
                                    open={isBillingRatesFormDialogOpen === freightLineItemIndex}
                                    onClose={() => setIsBillingRatesFormDialogOpen(undefined)}
                                    onConfirm={(formValues) => handleRatesUpdated(freightLineItemIndex, {
                                        ...freightBillingLineItem,
                                        ...formValues,
                                    })}
                                    billingRates={freightBillingLineItem}
                                    lineItem={orderReviewLineItem}
                                    isFob={order?.quote?.type === QuoteType.FOB}
                                    disabled={isDisabled(freightBillingLineItem)}
                                    isOrder={false}
                                    customerName={order?.quote?.customer?.name}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                                <FormInput
                                    label='Amount'
                                    name='amount'
                                    value={formatCurrency(freightBillingLineItem.salesPrice ?? freightBillingLineItem.quoteRate ?? 0)}
                                    onChange={() => {}}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                                <FormInput
                                    label={getIsFlatRateApplied(freightBillingLineItem) ? 'Hauler Rate (Flat)' : 'Hauler Rate'}
                                    name='haulerRate'
                                    value={formatCurrency(getHaulerRate(freightBillingLineItem))}
                                    onChange={() => {}}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                                <FormInput
                                    label='Hauler Total'
                                    name='haulerTotal'
                                    value={formatCurrency(getHaulerTotal(freightBillingLineItem))}
                                    onChange={() => {}}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                                <FormNumberInput
                                    label='Fuel Surcharge % (0-100)'
                                    name='fuelSurchargePercent'
                                    value={freightBillingLineItem.fuelSurchargePercent}
                                    onChange={(e) => handleFreightBillingLineItemChange(freightLineItemIndex, 'fuelSurchargePercent', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                                    fullWidth
                                    disabled={isDisabled(freightBillingLineItem) || freightBillingLineItem.pricingMethodType === PricingMethodType.MaterialOnly}
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
        </Box>
    );
};
